'use client';

import React from 'react';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import Link from 'next/link';
import { useLocale } from '@/app/components/LocaleProvider';
import PageHero from '@/app/components/PageHero';
import PageContainer from '@/app/components/PageContainer';
import PageSection from '@/app/components/PageSection';
import SectionTitle from '@/app/components/SectionTitle';
import GlassCard from '@/app/components/GlassCard';
import PageCta from '@/app/components/PageCta';
import { getPathWithLocale } from '@/lib/i18n';

const commissionTiers = [
    { rangeKey: 'referralTier1Range', percent: '2%' },
    { rangeKey: 'referralTier2Range', percent: '3%' },
    { rangeKey: 'referralTier3Range', percent: '4%' },
    { rangeKey: 'referralTier4Range', percent: '5%' },
    { rangeKey: 'referralTier5Range', percent: '6%' },
    { rangeKey: 'referralTier6Range', percent: '7%' },
];

export default function ReferralProgramPage() {
    const { t } = useComponentTranslations("Referral");
    const locale = useLocale();
    const isEn = locale.startsWith('en');

    const steps = [
        t('referralStep1', isEn ? 'Refer a client' : 'Приведите клиента'),
        t('referralStep2', isEn ? 'The client signs a contract' : 'Клиент заключает контракт'),
        t('referralStep3', isEn ? 'You get a share of the contract' : 'Получите процент от суммы контракта'),
    ];

    return (
        <div className="min-h-screen text-white">
            <PageContainer>
                <PageHero
                    id="referral-hero"
                    title={t('referralPageTitle', isEn ? 'Referral program' : 'Реферальная программа')}
                    description={t('referralPageSubtitle', isEn ? 'Get rewarded for recommending clients.' : 'Получайте вознаграждение за рекомендации клиентов.')}
                    badges={
                        isEn
                            ? ["Up to 20% commission", "Payouts in USDT / Fast wire"]
                            : ["До 20% commission", "Payouts in USDT / Fast wire"]
                    }
                    actions={[
                        {
                            href: getPathWithLocale("/register", locale),
                            label: isEn ? "Sign up" : "Зарегистрироваться",
                            primary: true,
                            ctaLocation: "pagehero_referral_primary",
                        },
                        {
                            href: getPathWithLocale("/dashboard/referrals", locale),
                            label: isEn ? "Referral dashboard" : "Войти в кабинет",
                            ctaLocation: "pagehero_referral_secondary",
                        },
                    ]}
                />
            </PageContainer>

            {/* Размер вознаграждения: таблица вместо слайдера — сравнение по числам
                читается сразу, на телефоне не нужно листать карусель. */}
            <PageSection>
                <SectionTitle
                    title={t('referralCommissionTitle', isEn ? 'Commission' : 'Размер вознаграждения')}
                    description={
                        isEn
                            ? "The share depends on the contract amount. The exact terms are fixed in the agreement."
                            : "Процент зависит от суммы контракта. Точные условия фиксируются в договоре."
                    }
                />
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    {commissionTiers.map((tier, index) => (
                        <GlassCard key={tier.percent}>
                            <div className="text-2xl font-bold text-white md:text-3xl">{tier.percent}</div>
                            <div className="mt-1 text-sm leading-snug text-white/60">
                                {t(tier.rangeKey, isEn ? `Tier ${index + 1}` : `Уровень ${index + 1}`)}
                            </div>
                        </GlassCard>
                    ))}
                </div>
            </PageSection>

            {/* Как это работает */}
            <PageSection tone="muted">
                <SectionTitle title={t('referralHowItWorksTitle', isEn ? 'How it works' : 'Как это работает')} />
                <ol className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {steps.map((step, index) => (
                        <li key={step}>
                            <GlassCard className="h-full">
                                <div className="text-2xl font-bold text-white/40">{index + 1}</div>
                                <p className="mt-2 text-sm leading-relaxed text-white/70">{step}</p>
                            </GlassCard>
                        </li>
                    ))}
                </ol>
            </PageSection>

            <PageCta
                title={isEn ? "Ready to recommend Dev48?" : "Готовы рекомендовать Dev48?"}
                description={
                    isEn
                        ? "Sign up, get your personal link and track payouts in the dashboard."
                        : "Зарегистрируйтесь, получите персональную ссылку и следите за выплатами в кабинете."
                }
                actions={[
                    {
                        href: getPathWithLocale("/dashboard/referrals", locale),
                        label: t('referralDashboardLink', isEn ? 'Referral dashboard' : 'Перейти в реферальный кабинет'),
                        primary: true,
                        ctaLocation: "pagecta_referral_primary",
                    },
                    {
                        href: getPathWithLocale("/referral/rules", locale),
                        label: t('referralRulesLink', isEn ? 'Program rules' : 'Правила программы'),
                        ctaLocation: "pagecta_referral_secondary",
                    },
                ]}
            >
                <p className="max-w-3xl text-xs leading-relaxed text-white/50">
                    {t('referralImportantText', isEn
                        ? 'Read the referral rules and payout limits before you start.'
                        : 'Перед началом работы ознакомьтесь с правилами реферальной программы и ограничениями по выплатам.')}{' '}
                    <Link
                        href={getPathWithLocale("/referral/rules", locale)}
                        className="underline underline-offset-4 transition-colors hover:text-white/80"
                    >
                        {t('referralImportantRulesLink', isEn ? 'Open the rules' : 'Открыть правила')}
                    </Link>
                </p>
            </PageCta>
        </div>
    );
}
